import React from "react";

import {
  BROKER_ALERT_DESC_CLASS,
} from "@/components/Broker/brokerAlertUi";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { ColumnMapping, ImportMode, LayoutShapeId } from "../../../types/customCsvImport";
import { sanitizeCsvCellDisplay } from "../../../utils/customCsvImport";
import { formatTradeDateTimeDetailForDisplay } from "../../../utils/displayDateTime";
import {
  formatSampleOrderFillLine,
  formatSampleOrderPl,
  getSampleOrderLeg,
  getSampleOrderPositionEffect,
  groupSampleOrdersIntoRoundTrips,
  shouldGroupSampleOrdersAsRoundTrips,
} from "../../../utils/sampleOrderPreview";

export interface SampleRowsTableProps {
  headers: string[];
  rows: Array<Record<string, string>>;
  columnMapping?: ColumnMapping;
  maxRows?: number;
}

/** Max CSV sample rows shown in preview — trades table matches this count. */
export const PREVIEW_SAMPLE_MAX_ROWS = 8;

const TABLE_HEAD_CLASS =
  "font-[family-name:var(--font-primary)] text-xs font-normal normal-case tracking-normal text-[color:var(--brand-tropical-surf)]";

const TABLE_CELL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500";

const MAPPED_HEAD_CLASS = cn(
  TABLE_HEAD_CLASS,
  "bg-[color:color-mix(in_oklab,var(--brand-tropical-surf)_10%,transparent)]"
);

const MAPPED_CELL_CLASS = cn(
  TABLE_CELL_CLASS,
  "bg-[color:color-mix(in_oklab,var(--brand-tropical-surf)_6%,transparent)]"
);

const TABLE_SHELL_CLASS =
  "w-full overflow-x-auto overflow-y-hidden rounded-[6px] border border-border bg-background";

function PreviewDataTableShell({
  children,
  "data-preview-table": dataPreviewTable,
}: {
  children: React.ReactNode;
  "data-preview-table"?: string;
}): React.ReactElement {
  return (
    <div
      data-shadcn-data-table
      data-custom-csv-preview-table=""
      data-preview-table={dataPreviewTable}
      className={TABLE_SHELL_CLASS}
    >
      {children}
    </div>
  );
}

const SampleRowsTable = ({
  headers,
  rows,
  columnMapping = {},
  maxRows = PREVIEW_SAMPLE_MAX_ROWS,
}: SampleRowsTableProps): React.ReactElement => {
  const mappedHeaders = new Set(
    Object.values(columnMapping)
      .map((h) => h.trim())
      .filter(Boolean)
  );

  const displayRows = rows.slice(0, maxRows);
  const displayHeaders = headers.length
    ? headers
    : displayRows.length
      ? Object.keys(displayRows[0])
      : [];

  if (!displayHeaders.length) {
    return (
      <p className={cn(BROKER_ALERT_DESC_CLASS, "m-0")}>No sample rows to display.</p>
    );
  }

  return (
    <PreviewDataTableShell data-preview-table="file-sample">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {displayHeaders.map((header) => {
              const mapped = mappedHeaders.has(header);
              return (
                <TableHead
                  key={header}
                  className={mapped ? MAPPED_HEAD_CLASS : TABLE_HEAD_CLASS}
                >
                  {header}
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayRows.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {displayHeaders.map((header) => {
                const mapped = mappedHeaders.has(header);
                return (
                  <TableCell
                    key={header}
                    className={cn("text-xs", mapped ? MAPPED_CELL_CLASS : TABLE_CELL_CLASS)}
                  >
                    {sanitizeCsvCellDisplay(String(row[header] ?? ""))}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </PreviewDataTableShell>
  );
};

function formatNumber(value: unknown): string {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString(undefined, { maximumFractionDigits: 5 });
}

function sampleOrderTime(order: Record<string, unknown>, timeZone: string): string {
  const timeRaw = String(order.transactionTime ?? order.createTime ?? "");
  return timeRaw ? formatTradeDateTimeDetailForDisplay(timeRaw, timeZone) : "—";
}

function sampleOrderSide(order: Record<string, unknown>): string {
  return String(order.type ?? order.side ?? "—");
}

function sampleOrderQty(order: Record<string, unknown>): string {
  const qty = order.quantity ?? getSampleOrderLeg(order)?.quantity;
  if (qty == null || qty === "") return "—";
  return formatNumber(qty);
}

function sampleOrderPrice(order: Record<string, unknown>): string {
  const price = order.price ?? getSampleOrderLeg(order)?.averagePrice;
  if (price == null || price === "") return "—";
  return formatNumber(price);
}

function PlCell({ order }: { order: Record<string, unknown> | undefined }): React.ReactElement {
  const pl = formatSampleOrderPl(order);
  if (!pl) {
    return <span className={TABLE_CELL_CLASS}>—</span>;
  }
  const numeric = Number(String(pl).replace(/^\+/, ""));
  const tone =
    Number.isFinite(numeric) && numeric < 0
      ? "text-red-600"
      : Number.isFinite(numeric) && numeric > 0
        ? "text-[color:var(--tessera-pnl-positive)]"
        : "text-gray-500";
  return <span className={cn(TABLE_CELL_CLASS, tone)}>{pl}</span>;
}

function SampleOrdersFootnote(props: {
  shown: number;
  totalBuilt?: number;
  unitLabel: string;
  fillCount?: number;
}): React.ReactElement | null {
  if (!props.totalBuilt && !props.fillCount) return null;
  if (props.unitLabel === "round trips" && props.fillCount && props.fillCount > props.shown) {
    return (
      <p className={cn(BROKER_ALERT_DESC_CLASS, "m-0 pt-2")}>
        Showing {props.shown} sample {props.unitLabel} · {props.fillCount} fills will be imported
        in total.
      </p>
    );
  }
  if (!props.totalBuilt || props.totalBuilt <= props.shown) return null;
  return (
    <p className={cn(BROKER_ALERT_DESC_CLASS, "m-0 pt-2")}>
      Showing {props.shown} sample {props.unitLabel} of {props.totalBuilt} the server would
      import.
    </p>
  );
}

function padToRowCount<T>(rows: T[], targetCount: number): Array<T | null> {
  if (targetCount <= 0) return [];
  const capped = rows.slice(0, targetCount);
  const result: Array<T | null> = [...capped];
  while (result.length < targetCount) {
    result.push(null);
  }
  return result;
}

const EMPTY_CELL = <span className={TABLE_CELL_CLASS}>—</span>;

export function SampleOrdersTable(props: {
  orders: Array<Record<string, unknown>>;
  importMode?: ImportMode;
  layoutShapeId?: LayoutShapeId | string;
  timeZone?: string;
  totalBuilt?: number;
  noSampleBuiltLabel?: string;
  /** Match the sample-from-file table row count for aligned preview height. */
  maxRows?: number;
}): React.ReactElement {
  const orders = props.orders;
  const timeZone = props.timeZone?.trim() || "UTC";
  const maxRows = props.maxRows ?? PREVIEW_SAMPLE_MAX_ROWS;
  const groupAsRoundTrips = shouldGroupSampleOrdersAsRoundTrips(
    props.importMode,
    props.layoutShapeId
  );

  if (!orders.length && maxRows <= 0) {
    return (
      <p className={cn(BROKER_ALERT_DESC_CLASS, "m-0")}>
        {props.noSampleBuiltLabel ?? "No sample trades built yet."}
      </p>
    );
  }

  if (groupAsRoundTrips) {
    const roundTrips = groupSampleOrdersIntoRoundTrips(orders);
    const actualShown = Math.min(roundTrips.length, maxRows);
    const displayRoundTrips = padToRowCount(roundTrips, maxRows);
    return (
      <>
        <PreviewDataTableShell data-preview-table="built-trades">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className={TABLE_HEAD_CLASS}>Instrument</TableHead>
                <TableHead className={TABLE_HEAD_CLASS}>Ref</TableHead>
                <TableHead className={TABLE_HEAD_CLASS}>Open</TableHead>
                <TableHead className={TABLE_HEAD_CLASS}>Close</TableHead>
                <TableHead className={TABLE_HEAD_CLASS}>P/L</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayRoundTrips.map((trip, index) => {
                if (!trip) {
                  return (
                    <TableRow key={`placeholder-${index}`} aria-hidden>
                      <TableCell>{EMPTY_CELL}</TableCell>
                      <TableCell>{EMPTY_CELL}</TableCell>
                      <TableCell>{EMPTY_CELL}</TableCell>
                      <TableCell>{EMPTY_CELL}</TableCell>
                      <TableCell>{EMPTY_CELL}</TableCell>
                    </TableRow>
                  );
                }
                return (
                  <TableRow key={trip.positionCode}>
                    <TableCell className={cn(TABLE_CELL_CLASS, "whitespace-nowrap")}>
                      {trip.instrument}
                    </TableCell>
                    <TableCell className={TABLE_CELL_CLASS}>{trip.reference}</TableCell>
                    <TableCell className={cn(TABLE_CELL_CLASS, "min-w-[12rem]")}>
                      {trip.open
                        ? formatSampleOrderFillLine(trip.open, "Open", timeZone)
                        : "—"}
                    </TableCell>
                    <TableCell className={cn(TABLE_CELL_CLASS, "min-w-[12rem]")}>
                      {trip.close
                        ? formatSampleOrderFillLine(trip.close, "Close", timeZone)
                        : "Open only in this sample"}
                    </TableCell>
                    <TableCell>
                      <PlCell order={trip.close ?? trip.open} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </PreviewDataTableShell>
        {actualShown > 0 ? (
          <SampleOrdersFootnote
            shown={actualShown}
            totalBuilt={props.totalBuilt}
            unitLabel="round trips"
            fillCount={props.totalBuilt}
          />
        ) : null}
      </>
    );
  }

  const actualShown = Math.min(orders.length, maxRows);
  const displayFills = padToRowCount(orders, maxRows);
  return (
    <>
      <PreviewDataTableShell data-preview-table="built-fills">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className={TABLE_HEAD_CLASS}>Instrument</TableHead>
              <TableHead className={TABLE_HEAD_CLASS}>Effect</TableHead>
              <TableHead className={TABLE_HEAD_CLASS}>Time</TableHead>
              <TableHead className={TABLE_HEAD_CLASS}>Side</TableHead>
              <TableHead className={TABLE_HEAD_CLASS}>Qty</TableHead>
              <TableHead className={TABLE_HEAD_CLASS}>Price</TableHead>
              <TableHead className={TABLE_HEAD_CLASS}>P/L</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayFills.map((order, index) => {
              if (!order) {
                return (
                  <TableRow key={`placeholder-${index}`} aria-hidden>
                    <TableCell>{EMPTY_CELL}</TableCell>
                    <TableCell>{EMPTY_CELL}</TableCell>
                    <TableCell>{EMPTY_CELL}</TableCell>
                    <TableCell>{EMPTY_CELL}</TableCell>
                    <TableCell>{EMPTY_CELL}</TableCell>
                    <TableCell>{EMPTY_CELL}</TableCell>
                    <TableCell>{EMPTY_CELL}</TableCell>
                  </TableRow>
                );
              }
              const instrument = String(order.instrument ?? order.symbol ?? "—");
              const effect = getSampleOrderPositionEffect(order) || "FILL";
              const id = String(order.id ?? index);
              return (
                <TableRow key={id}>
                  <TableCell className={cn(TABLE_CELL_CLASS, "whitespace-nowrap")}>
                    {instrument}
                  </TableCell>
                  <TableCell className={TABLE_CELL_CLASS}>{effect}</TableCell>
                  <TableCell className={cn(TABLE_CELL_CLASS, "whitespace-nowrap")}>
                    {sampleOrderTime(order, timeZone)}
                  </TableCell>
                  <TableCell className={TABLE_CELL_CLASS}>{sampleOrderSide(order)}</TableCell>
                  <TableCell className={TABLE_CELL_CLASS}>{sampleOrderQty(order)}</TableCell>
                  <TableCell className={TABLE_CELL_CLASS}>{sampleOrderPrice(order)}</TableCell>
                  <TableCell>
                    <PlCell order={order} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </PreviewDataTableShell>
      {actualShown > 0 ? (
        <SampleOrdersFootnote
          shown={actualShown}
          totalBuilt={props.totalBuilt}
          unitLabel="fills"
        />
      ) : null}
    </>
  );
}

/** @deprecated Use SampleOrdersTable — kept for any legacy imports. */
export function SampleOrdersCards(
  props: Parameters<typeof SampleOrdersTable>[0]
): React.ReactElement {
  return <SampleOrdersTable {...props} />;
}

export default SampleRowsTable;
